import { SpgPricingType } from '../../API/Enums';
import { formatAsCurrency } from '../../helpers/Number';
import { useLocalization, useLocalizationExtension } from '../../context/Localization/localization';
import css from './SpgPriceText.module.scss';
import type { GoMotoComponent } from '../../API/GoMoto.types';
import type { SpgPriceTextProps } from './SpgPriceText.types';

export const SpgPriceText: GoMotoComponent<SpgPriceTextProps> = ({
    spgPricing = null,
    priceLow = null,
    priceHigh = null,
    price = null,
    showSingleWhenNoRange = true,
    useSpgPricingLogic = true,
    markOut = false,
}) => {
    const Localization = useLocalization();
    const { formatAsString } = useLocalizationExtension();
    let result: string = Localization.AskUsAboutPricing;

    if (!useSpgPricingLogic) {
        result = formatAsCurrency(price, true);
    }
    else {
        const hasLow: boolean = priceLow !== null;
        const hasHigh: boolean = priceHigh !== null;
        const hasSame: boolean = hasLow && hasHigh && priceLow === priceHigh;
        const hasRange: boolean = hasLow && hasHigh && !hasSame && priceLow !== priceHigh;
        const singleSpgPrice: number | null = hasSame || (hasLow && !hasHigh) || (!hasLow && hasHigh) ? priceLow ?? priceHigh ?? price : null;

        // if explicitly no price, ALWAYS return "Ask Us About Pricing" regardless of SPG or Admin site price data
        if (spgPricing === SpgPricingType.NoPrice) {
            result = Localization.AskUsAboutPricing;
        }
        // fallback to Admin site price if available (for services), otherwise "Ask Us About Pricing"
        else if (!hasRange && singleSpgPrice === null) {
            if (price !== null) {
                result = formatAsCurrency(price);
            }
            else {
                result = Localization.AskUsAboutPricing;
            }
        }
        // show single SPG price when only the one exists
        else if (singleSpgPrice !== null && (showSingleWhenNoRange || hasSame)) {
            result = formatAsCurrency(singleSpgPrice);
        }
        else {
            switch (spgPricing) {
                case SpgPricingType.Range:
                    if (hasLow && hasHigh) {
                        result = formatAsString(Localization.AToB, formatAsCurrency(priceLow), formatAsCurrency(priceHigh));
                    }
                    break;
                case SpgPricingType.Minimum:
                    if (hasLow) {
                        result = formatAsString(Localization.StartingAt, formatAsCurrency(priceLow));
                    }
                    break;
                case SpgPricingType.Maximum:
                    if (hasHigh) {
                        result = formatAsString(Localization.UpTo, formatAsCurrency(priceHigh));
                    }
                    break;
            }
        }
    }

    return markOut ? (
        <div className={css.markoutPrice}>
            {result}
        </div>
    ) : (
        <>{result}</>
    );
};
